
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import { getConversations, getConversation, getConversationAudio } from "@/services/ai/elevenLabsService/callStatusService";
import { findLeadByPhone } from "@/services/leads";
import { supabase } from "@/integrations/supabase/client";

export interface Conversation {
  id: string;
  conversation_id?: string;
  status: string;
  creation_time?: string;
  completion_time?: string;
  call_sid?: string;
  to_number?: string;
  agent_id?: string;
  lead_id?: string;
  lead_name?: string;
  dealer_id?: string;
  dealer_name?: string;
  meta?: Record<string, any>;
  // Support the fields from the API response
  start_time_unix_secs?: number;
  call_duration_secs?: number;
  duration_seconds?: number;
}

export const useConversationsData = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [rawResponse, setRawResponse] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState<Record<string, boolean>>({});
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  
  const saveConversationToSupabase = async (conversation: any) => {
    try {
      // Get key data from the conversation object
      const phoneNumber = conversation.meta?.to_number || 
                          conversation.meta?.outbound_call_payload?.to_number ||
                          null;
                          
      const conversationId = conversation.conversation_id || conversation.id;
      
      // Check if this conversation already exists in Supabase
      const { data: existingRecord, error: checkError } = await supabase
        .from("conversation_records")
        .select("id")
        .eq("conversation_id", conversationId)
        .maybeSingle();
        
      if (checkError) {
        console.error("Error checking for existing conversation:", checkError);
        return;
      }
      
      // If the conversation already exists, don't save it again
      if (existingRecord) {
        console.log(`Conversation ${conversationId} already exists in Supabase`);
        return;
      }
      
      // Extract relevant data for storing in Supabase
      const recordData = {
        conversation_id: conversationId,
        external_number: phoneNumber,
        dealership_name: conversation.meta?.dealership_name || "Unknown Dealership",
        agent_name: conversation.meta?.agent_name || null,
        lead_first_name: conversation.meta?.lead_first_name || null,
        call_start_time: conversation.creation_time || 
                        (conversation.start_time_unix_secs ? new Date(conversation.start_time_unix_secs * 1000).toISOString() : null),
        call_duration_secs: conversation.call_duration_secs || conversation.duration_seconds || null,
        outcome: conversation.outcome || conversation.status || null,
        audio_url: conversation.audio_url || null,
        audio_fetched: !!conversation.audio_url,
        call_sid: conversation.call_sid || null,
        metadata_json: conversation
      };
      
      // Save to Supabase
      const { error: insertError } = await supabase
        .from("conversation_records")
        .insert([recordData]);
        
      if (insertError) {
        console.error("Error saving conversation to Supabase:", insertError);
      } else {
        console.log(`Saved conversation ${conversationId} to Supabase`);
      }
    } catch (error) {
      console.error("Error in saveConversationToSupabase:", error);
    }
  };
  
  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      const result = await getConversations(20);
      setRawResponse(result); // Store raw response for debugging
      
      if (result.success) {
        // Save each conversation to Supabase
        result.conversations.forEach((conv: any) => {
          saveConversationToSupabase(conv);
        });
        
        const enhancedConversations = await Promise.all(
          result.conversations.map(async (conv: any) => {
            const toNumber = conv.meta?.to_number || 
                            conv.meta?.outbound_call_payload?.to_number ||
                            null;
            
            let leadData = null;
            if (toNumber) {
              const leadResult = await findLeadByPhone(toNumber);
              if (leadResult.success && leadResult.lead) {
                leadData = leadResult.lead;
              }
            }
            
            // Map conversation_id to id for consistent access
            const id = conv.conversation_id || conv.id;
            
            return {
              ...conv,
              id: id, // Ensure id is set
              conversation_id: conv.conversation_id || id,
              to_number: toNumber,
              lead_id: leadData?.id || null,
              lead_name: leadData?.name || null,
              dealer_id: leadData?.dealer_id || null,
              dealer_name: null,
              // Map timestamps if needed
              creation_time: conv.creation_time || 
                (conv.start_time_unix_secs ? new Date(conv.start_time_unix_secs * 1000).toISOString() : undefined),
              // Ensure all needed fields are present
              start_time_unix_secs: conv.start_time_unix_secs,
              call_duration_secs: conv.call_duration_secs,
              duration_seconds: conv.duration_seconds,
            };
          })
        );
        
        setConversations(enhancedConversations);
      } else {
        toast({
          title: "Error fetching conversations",
          description: result.error,
          variant: "destructive"
        });

        // If ElevenLabs API fails, try to load from Supabase
        fetchConversationsFromSupabase();
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      toast({
        title: "An unexpected error occurred",
        description: error instanceof Error ? error.message : "Failed to fetch conversations",
        variant: "destructive"
      });
      
      // If API call fails, try to load from Supabase
      fetchConversationsFromSupabase();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchConversationsFromSupabase = async () => {
    try {
      console.log("Fetching conversations from Supabase database");
      
      const { data, error } = await supabase
        .from("conversation_records")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);
        
      if (error) {
        throw error;
      }
      
      if (data && data.length > 0) {
        const formattedConversations = data.map(record => {
          // Extract metadata from JSON if available, or use the record directly
          const metadata = record.metadata_json || {};
          
          return {
            id: record.conversation_id || record.id,
            conversation_id: record.conversation_id,
            status: record.outcome || record.termination_reason || "unknown",
            creation_time: record.call_start_time,
            call_duration_secs: record.call_duration_secs,
            to_number: record.external_number,
            agent_name: record.agent_name,
            lead_name: record.lead_first_name,
            dealership_name: record.dealership_name,
            audio_url: record.audio_url,
            meta: metadata
          };
        });
        
        setConversations(formattedConversations);
        console.log(`Loaded ${formattedConversations.length} conversations from Supabase`);
      } else {
        console.log("No conversations found in Supabase");
      }
    } catch (error) {
      console.error("Error fetching conversations from Supabase:", error);
    }
  };

  const handleLinkToLead = async (conversationId: string, phoneNumber: string) => {
    if (!phoneNumber) {
      toast({
        title: "Cannot link conversation",
        description: "No phone number associated with this conversation",
        variant: "destructive"
      });
      return;
    }

    setIsLinking(prev => ({ ...prev, [conversationId]: true }));
    
    try {
      const leadResult = await findLeadByPhone(phoneNumber);
      
      if (!leadResult.success || !leadResult.lead) {
        toast({
          title: "No lead found",
          description: `No lead found with phone number ${phoneNumber}`,
          variant: "destructive"
        });
        return;
      }
      
      // Update conversations in state
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { 
              ...conv, 
              lead_id: leadResult.lead.id, 
              lead_name: leadResult.lead.name,
              dealer_id: leadResult.lead.dealer_id
            }
          : conv
      ));
      
      // Also update the conversation record in Supabase
      const { error } = await supabase
        .from("conversation_records")
        .update({
          lead_first_name: leadResult.lead.name
        })
        .eq("conversation_id", conversationId);
        
      if (error) {
        console.error("Error updating conversation in Supabase:", error);
      }
      
      toast({
        title: "Conversation linked",
        description: `Successfully linked to ${leadResult.lead.name}`,
        variant: "default"
      });
      
    } catch (error) {
      console.error("Error linking conversation to lead:", error);
      toast({
        title: "Error linking conversation",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsLinking(prev => ({ ...prev, [conversationId]: false }));
    }
  };

  // Filter conversations based on search term and status
  const getFilteredConversations = () => {
    return conversations.filter(conv => {
      if (statusFilter !== "all" && conv.status?.toLowerCase() !== statusFilter.toLowerCase()) {
        return false;
      }
      
      if (searchTerm.trim() !== "") {
        const term = searchTerm.toLowerCase();
        return (
          (conv.lead_name?.toLowerCase().includes(term)) || 
          (conv.to_number?.includes(term)) ||
          (conv.id?.toLowerCase().includes(term))
        );
      }
      
      return true;
    });
  };

  // Initial fetch
  useEffect(() => {
    fetchConversations();
  }, []);

  return {
    conversations,
    filteredConversations: getFilteredConversations(),
    rawResponse,
    isLoading,
    isLinking,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    fetchConversations,
    handleLinkToLead
  };
};
